import { useEffect, useMemo, useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import "./index.css";

// Import assets from data directory
// Types are provided by src/types/json.d.ts
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import analysis from "../data/analysis.json";
const videoUrl = "https://clippub.vibecoding.icu/onboarding-demo.mp4";

type TimestampedNote = { at?: string; note?: string };
type LinkItem = { label?: string; url?: string; at?: string };
type BehaviorItem = {
  timeRange: string; // "MM:SS - MM:SS"
  operations?: string[];
  actions?: { timestamp?: string; action?: string }[];
  analysis?: {
    smoothPoints?: string;
    frictionPoints?: string;
    smoothPointsAndInterests?: string;
    difficultiesAndConfusion?: string;
  };
  // Optional, to adapt to richer JSON
  title?: string;
  sceneTitle?: string;
  summary?: string;
  highlights?: string[];
  metrics?: Record<string, string | number>;
  annotations?: TimestampedNote[];
  links?: LinkItem[];
};

type AnalysisJson = {
  reportTitle?: string;
  reportTarget?: string;
  objective?: string;
  sections?: {
    userBehaviorRecap?: BehaviorItem[];
    onboardingDesignAnalysis?: {
      informationArchitectureAndFlow?: { pros?: string[]; cons?: string[] };
      guidanceAndEducation?: { pros?: string[]; cons?: string[] };
      uiAndCopywriting?: { pros?: string[]; cons?: string[] };
      valueProposition?: { pros?: string[]; cons?: string[] };
    };
    actionableRecommendations?: {
      id?: number | string;
      title?: string;
      problem?: string;
      recommendation?: string;
      expectedOutcome?: string;
    }[];
  };
  userBehaviorRecap?: BehaviorItem[]; // fallback to old schema
  onboardingDesignAnalysis?: AnalysisJson["sections"]["onboardingDesignAnalysis"]; // fallback
  actionableRecommendations?: NonNullable<AnalysisJson["sections"]>["actionableRecommendations"]; // fallback
};

function parseTimeToSeconds(t: string): number {
  // Supports M:SS or MM:SS
  const [m, s] = t.trim().split(":").map(Number);
  if (Number.isNaN(m) || Number.isNaN(s)) return 0;
  return m * 60 + s;
}

export function App() {
  const DRAWER_HANDLE = 48;
  const data = analysis as AnalysisJson;
  const items = (data.sections?.userBehaviorRecap ?? data.userBehaviorRecap ?? []) as BehaviorItem[];

  const segments = useMemo(
    () =>
      items.map((it, idx) => {
        const [start, end] = it.timeRange.split("-").map(s => s.trim());
        return {
          index: idx,
          startLabel: start,
          endLabel: end,
          startSec: parseTimeToSeconds(start),
          endSec: parseTimeToSeconds(end),
          item: it,
        };
      }),
    [items],
  );

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const headerRef = useRef<HTMLDivElement | null>(null);
  const infoRef = useRef<HTMLDivElement | null>(null);
  const rightListRef = useRef<HTMLDivElement | null>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const [activeActionIndex, setActiveActionIndex] = useState<number | null>(null);
  const activeRefMap = useRef<Record<number, HTMLDivElement | null>>({});
  const actionRefMap = useRef<Record<number, Record<number, HTMLLIElement | null>>>({});
  const [videoHeight, setVideoHeight] = useState<number>();
  const [isSeeking, setIsSeeking] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const lastReportedTimeRef = useRef(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerHeight, setDrawerHeight] = useState<number>(() => Math.round((typeof window !== "undefined" ? window.innerHeight : 800) * 0.7));
  const [isMobilePortrait, setIsMobilePortrait] = useState<boolean>(() =>
    typeof window !== "undefined" ? window.matchMedia("(orientation: portrait)").matches && window.innerWidth < 768 : false,
  );

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const update = () => {
      const t = v.currentTime;
      if (isSeeking || Math.abs(t - lastReportedTimeRef.current) >= 0.2) {
        lastReportedTimeRef.current = t;
        setCurrentTime(t);
      }
    };
    const onSeeking = () => {
      setIsSeeking(true);
      update();
    };
    const onSeeked = () => {
      setIsSeeking(false);
      update();
    };
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    v.addEventListener("timeupdate", update);
    v.addEventListener("seeking", onSeeking);
    v.addEventListener("seeked", onSeeked);
    v.addEventListener("play", onPlay);
    v.addEventListener("pause", onPause);
    v.addEventListener("ended", onPause);
    return () => {
      v.removeEventListener("timeupdate", update);
      v.removeEventListener("seeking", onSeeking);
      v.removeEventListener("seeked", onSeeked);
      v.removeEventListener("play", onPlay);
      v.removeEventListener("pause", onPause);
      v.removeEventListener("ended", onPause);
    };
  }, [isSeeking]);

  useEffect(() => {
    const computeHeight = () => {
      const headerH = headerRef.current?.offsetHeight ?? 0;
      const infoH = infoRef.current?.offsetHeight ?? 0;
      const paddings = 16; // p-4 on CardContent
      // page padding from outer wrapper (p-4 / p-6)
      const wrap = wrapperRef.current;
      let pagePad = 0;
      if (wrap) {
        const cs = getComputedStyle(wrap);
        pagePad = parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom);
      }
      const drawerVisible = drawerOpen ? drawerHeight : DRAWER_HANDLE;
      const breathing = 8;
      const h = Math.max(220, window.innerHeight - headerH - infoH - pagePad - paddings - breathing - drawerVisible);
      setVideoHeight(h);
      setDrawerHeight(Math.round(window.innerHeight * 0.7));
    };
    computeHeight();
    window.addEventListener("resize", computeHeight);
    return () => window.removeEventListener("resize", computeHeight);
  }, [drawerOpen, drawerHeight]);

  // Detect mobile portrait to switch to compact layout (video top 2/3, analysis bottom 1/3)
  useEffect(() => {
    const update = () => {
      const portrait = window.matchMedia("(orientation: portrait)").matches;
      setIsMobilePortrait(portrait && window.innerWidth < 768);
    };
    update();
    window.addEventListener("resize", update);
    window.matchMedia("(orientation: portrait)").addEventListener("change", update);
    return () => {
      window.removeEventListener("resize", update);
      window.matchMedia("(orientation: portrait)").removeEventListener("change", update);
    };
  }, []);

  const ensureVisible = (
    container: HTMLElement | null,
    el: HTMLElement | null,
    opts?: { top?: number; bottom?: number; behavior?: ScrollBehavior },
  ) => {
    if (!container || !el) return;
    const topPad = opts?.top ?? 12;
    const bottomPad = opts?.bottom ?? 12;
    const cRect = container.getBoundingClientRect();
    const eRect = el.getBoundingClientRect();
    // Negative => above; Positive => below
    const overTop = eRect.top - cRect.top - topPad;
    const overBottom = eRect.bottom - cRect.bottom + bottomPad;
    let delta = 0;
    if (overTop < 0) delta = overTop; else if (overBottom > 0) delta = overBottom;
    if (delta !== 0) {
      container.scrollBy({ top: delta, behavior: opts?.behavior ?? "auto" });
    }
  };

  useEffect(() => {
    // determine active segment by currentTime
    const t = currentTime;
    const idx = segments.findIndex(seg => t >= seg.startSec && t < seg.endSec);
    setActiveIndex(idx === -1 ? Math.max(0, segments.length - 1) : idx);
  }, [currentTime, segments]);

  // Compute active action index when time or segment changes
  useEffect(() => {
    const seg = segments[activeIndex];
    if (seg && Array.isArray(seg.item.actions) && seg.item.actions.length > 0) {
      const times = seg.item.actions
        .map(a => (a.timestamp ? parseTimeToSeconds(a.timestamp) : null))
        .map(v => (v === null || Number.isNaN(v) ? null : v));
      let aIdx: number | null = null;
      for (let i = 0; i < times.length; i++) {
        const tt = times[i];
        if (tt === null) continue;
        if (tt <= currentTime) aIdx = i; else break;
      }
      setActiveActionIndex(prev => (prev !== aIdx ? aIdx : prev));
    } else {
      setActiveActionIndex(prev => (prev !== null ? null : prev));
    }
  }, [currentTime, activeIndex, segments]);

  // Scroll segment into view when active segment changes
  useEffect(() => {
    const segEl = activeRefMap.current[activeIndex];
    ensureVisible(rightListRef.current, segEl, {
      top: 12,
      bottom: (drawerOpen ? drawerHeight : DRAWER_HANDLE) + 12,
      behavior: isSeeking ? "auto" : "smooth",
    });
  }, [activeIndex, drawerOpen, drawerHeight, isSeeking]);

  // Scroll active action into view when it changes
  useEffect(() => {
    if (activeActionIndex === null) return;
    const actEl = actionRefMap.current[activeIndex]?.[activeActionIndex];
    ensureVisible(rightListRef.current, actEl, {
      top: 72,
      bottom: (drawerOpen ? drawerHeight : DRAWER_HANDLE) + 12,
      behavior: isSeeking ? "auto" : "smooth",
    });
  }, [activeActionIndex, activeIndex, drawerOpen, drawerHeight, isSeeking]);

  const seekTo = (sec: number) => {
    const v = videoRef.current;
    if (!v) return;
    const shouldResume = !v.paused && !v.ended;
    v.currentTime = sec;
    if (shouldResume) void v.play();
  };

  const activeSeg = segments[activeIndex];

  // Mobile portrait layout
  if (isMobilePortrait) {
    const seg = segments[activeIndex];

    const getActions = (sIdx: number) => (segments[sIdx]?.item.actions ?? []) as { timestamp?: string; action?: string }[];
    const canPrev = (() => {
      if (!segments.length) return false;
      if (activeActionIndex !== null && activeActionIndex > 0) return true;
      for (let i = activeIndex - 1; i >= 0; i--) if (getActions(i).length > 0) return true;
      return false;
    })();
    const canNext = (() => {
      if (!segments.length) return false;
      const acts = getActions(activeIndex);
      if (acts.length && activeActionIndex !== null && activeActionIndex < acts.length - 1) return true;
      for (let i = activeIndex + 1; i < segments.length; i++) if (getActions(i).length > 0) return true;
      return false;
    })();

    const goPrevAction = () => {
      if (!segments.length) return;
      let s = activeIndex;
      let a = activeActionIndex ?? 0;
      const actsHere = getActions(s);
      if (actsHere.length && a > 0) a = a - 1; else {
        // find previous segment with actions
        for (let i = s - 1; i >= 0; i--) {
          const acts = getActions(i);
          if (acts.length) {
            s = i;
            a = acts.length - 1;
            break;
          }
        }
      }
      const targetActs = getActions(s);
      if (!targetActs.length) return;
      const ts = targetActs[a]?.timestamp;
      setActiveIndex(s);
      if (ts) seekTo(parseTimeToSeconds(ts));
    };
    const goNextAction = () => {
      if (!segments.length) return;
      let s = activeIndex;
      let a = activeActionIndex ?? -1; // if null, jump to first
      const actsHere = getActions(s);
      if (actsHere.length && a < actsHere.length - 1) a = Math.max(0, a + 1); else {
        // find next segment with actions
        for (let i = s + 1; i < segments.length; i++) {
          const acts = getActions(i);
          if (acts.length) {
            s = i;
            a = 0;
            break;
          }
        }
      }
      const targetActs = getActions(s);
      if (!targetActs.length) return;
      const ts = targetActs[a]?.timestamp;
      setActiveIndex(s);
      if (ts) seekTo(parseTimeToSeconds(ts));
    };

    return (
      <div className="h-dvh w-full overflow-hidden">
        {/* Top 2/3: Video */}
        <div className="h-[66dvh] p-4 pb-2">
          <Card className="h-full">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">录屏视频</CardTitle>
              {seg && <CardDescription>当前区段：{seg.item.timeRange}</CardDescription>}
            </CardHeader>
            <CardContent className="pt-0 h-[calc(100%-64px)]">
              <video
                ref={videoRef}
                src={videoUrl}
                className="w-full h-full rounded-lg object-contain"
                controls
                playsInline
                preload="metadata"
              />
            </CardContent>
          </Card>
        </div>

        {/* Bottom 1/3: Current analysis with prev/next action controls */}
        <div className="h-[34dvh] p-4 pt-2">
          <Card className="relative h-full">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between gap-2">
                <CardTitle className="text-base">区段 {activeIndex + 1}</CardTitle>
                <span className="text-xs text-muted-foreground">{seg?.item.timeRange}</span>
              </div>
            </CardHeader>
            <CardContent className="pt-0 h-[calc(100%-64px)] overflow-hidden flex flex-col">
              {/* Controls + current action line */}
              <div className="shrink-0 flex items-center justify-between gap-2">
                <Button size="sm" variant="secondary" onClick={goPrevAction} disabled={!canPrev}>
                  上一条
                </Button>
                <div className="flex-1 text-center px-2">
                  {(() => {
                    const acts = seg?.item.actions ?? [];
                    const a = activeActionIndex ?? (acts.length ? 0 : -1);
                    const current = a >= 0 ? acts[a] : undefined;
                    if (current) {
                      return (
                        <div className="text-sm">
                          {current.timestamp && (
                            <button
                              className="inline-block align-middle rounded bg-muted px-2 py-0.5 mr-2 text-xs"
                              onClick={() => seekTo(parseTimeToSeconds(current.timestamp!))}
                            >
                              {current.timestamp}
                            </button>
                          )}
                          <span className="align-middle break-words">{current.action}</span>
                        </div>
                      );
                    }
                    const op = seg?.item.operations?.[0];
                    if (op) return <div className="text-sm">{op}</div>;
                    return <div className="text-sm text-muted-foreground">本区段暂无细粒度操作</div>;
                  })()}
                </div>
                <Button size="sm" variant="secondary" onClick={goNextAction} disabled={!canNext}>
                  下一条
                </Button>
              </div>

              {/* Analysis details scroll area */}
              <div className="mt-3 grow overflow-auto text-sm leading-relaxed space-y-3">
                {seg?.item.analysis && (
                  <div className="grid grid-cols-1 gap-2">
                    <div>
                      <div className="text-xs font-medium text-muted-foreground mb-1">顺畅点</div>
                      <p>{seg.item.analysis.smoothPoints ?? seg.item.analysis.smoothPointsAndInterests ?? "-"}</p>
                    </div>
                    <div>
                      <div className="text-xs font-medium text-muted-foreground mb-1">摩擦点</div>
                      <p>{seg.item.analysis.frictionPoints ?? seg.item.analysis.difficultiesAndConfusion ?? "-"}</p>
                    </div>
                  </div>
                )}

                {Array.isArray(seg?.item.highlights) && seg!.item.highlights!.length > 0 && (
                  <div>
                    <div className="text-xs font-medium text-muted-foreground mb-1">高光时刻</div>
                    <ul className="list-disc pl-5 space-y-1">
                      {seg!.item.highlights!.map((h, i) => (
                        <li key={i}>{h}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {seg?.item.metrics && Object.keys(seg.item.metrics).length > 0 && (
                  <div>
                    <div className="text-xs font-medium text-muted-foreground mb-1">指标</div>
                    <div className="grid grid-cols-2 gap-2">
                      {Object.entries(seg.item.metrics).map(([k, v]) => (
                        <div key={k} className="flex items-center justify-between gap-2 border rounded-md px-2 py-1">
                          <span className="text-muted-foreground">{k}</span>
                          <span className="font-medium">{String(v)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {Array.isArray(seg?.item.annotations) && seg!.item.annotations!.length > 0 && (
                  <div>
                    <div className="text-xs font-medium text-muted-foreground mb-1">时间批注</div>
                    <ul className="space-y-1">
                      {seg!.item.annotations!.map((a, i) => (
                        <li key={i} className="flex items-center gap-2">
                          {a.at && (
                            <Button size="sm" variant="outline" onClick={() => seekTo(parseTimeToSeconds(a.at!))}>
                              {a.at}
                            </Button>
                          )}
                          <span className="text-left">{a.note}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {Array.isArray(seg?.item.links) && seg!.item.links!.length > 0 && (
                  <div>
                    <div className="text-xs font-medium text-muted-foreground mb-1">相关链接</div>
                    <ul className="list-disc pl-5 space-y-1">
                      {seg!.item.links!.map((l, i) => (
                        <li key={i} className="flex items-center gap-2">
                          {l.at && (
                            <Button size="sm" variant="outline" onClick={() => seekTo(parseTimeToSeconds(l.at!))}>
                              {l.at}
                            </Button>
                          )}
                          {l.url ? (
                            <a className="text-primary underline underline-offset-4" href={l.url} target="_blank" rel="noreferrer">
                              {l.label || l.url}
                            </a>
                          ) : (
                            <span>{l.label}</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Desktop / landscape layout
  return (
    <div ref={wrapperRef} className="h-dvh w-full overflow-hidden p-4 md:p-6">
      <div className="grid grid-cols-1 md:[grid-template-columns:360px_1fr] lg:[grid-template-columns:420px_1fr] h-full items-start gap-4 max-w-[1200px] mx-auto w-full">
        {/* Left: Video */}
        <Card className="md:sticky md:top-0 self-start">
          <CardHeader ref={headerRef}>
            <CardTitle>录屏视频</CardTitle>
            {activeSeg && (
              <CardDescription>
                当前区段：{activeSeg.item.timeRange}
              </CardDescription>
            )}
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <video
              ref={videoRef}
              src={videoUrl}
              className="w-full rounded-lg object-contain mx-auto"
              style={videoHeight ? { height: `${videoHeight}px` } : undefined}
              controls
              playsInline
              preload="metadata"
            />
            <div ref={infoRef} className="flex items-center gap-2 mt-3 text-sm text-muted-foreground">
              <span>当前时间：{Math.floor(currentTime / 60).toString().padStart(2, "0")}:{Math.floor(currentTime % 60)
                .toString()
                .padStart(2, "0")}</span>
              {activeSeg && (
                <>
                  <span>·</span>
                  <span>
                    {activeSeg.startLabel} → {activeSeg.endLabel}
                  </span>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Right: Time-synced recap list */}
        <div
          className="flex flex-col gap-3 h-dvh overflow-auto pr-1 p-4"
          style={{ paddingBottom: (drawerOpen ? drawerHeight : DRAWER_HANDLE) + 8 }}
          ref={rightListRef}
        >
          <div className="mb-2">
            <h1 className="text-xl font-semibold leading-tight">{data.reportTitle ?? "用户行为回顾"}</h1>
            {(data.reportTarget || data.objective) && (
              <p className="text-muted-foreground text-sm mt-1">{data.reportTarget ?? data.objective}</p>
            )}
          </div>
          {segments.map(seg => (
            <Card
              key={seg.index}
              ref={el => (activeRefMap.current[seg.index] = el)}
              className={
                seg.index === activeIndex
                  ? "border-primary ring-2 ring-primary/30"
                  : "hover:border-muted-foreground/30"
              }
            >
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between gap-2">
                  <CardTitle className="text-base">
                    {seg.item.title || seg.item.sceneTitle || `区段 ${seg.index + 1}`}
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">{seg.item.timeRange}</span>
                    <Button size="sm" variant="outline" onClick={() => seekTo(seg.startSec)}>
                      跳到此段
                    </Button>
                  </div>
                </div>
                {seg.item.summary && (
                  <CardDescription className="mt-1 text-sm">{seg.item.summary}</CardDescription>
                )}
                {seg.index === activeIndex && (
                  <div className="mt-2 h-1 w-full rounded bg-muted overflow-hidden">
                    <div
                      className="h-full bg-primary transition-[width] duration-200"
                      style={{
                        width: `${Math.max(0, Math.min(1, (currentTime - seg.startSec) / Math.max(1, seg.endSec - seg.startSec))) * 100}%`,
                      }}
                    />
                  </div>
                )}
              </CardHeader>
              <CardContent className="pt-0">
                {(Array.isArray(seg.item.operations) || Array.isArray(seg.item.actions)) && (
                  <div className="mb-2">
                    <div className="text-xs font-medium text-muted-foreground mb-1">关键操作</div>
                    <ul className="space-y-1 text-sm text-left">
                      {(seg.item.operations ?? []).map((op, i) => (
                        <li key={"op-" + i} className="pl-2 py-1 rounded hover:bg-accent/60">
                          <span>{op}</span>
                        </li>
                      ))}
                      {(seg.item.actions ?? []).map((a, i) => {
                        const ts = a.timestamp ? parseTimeToSeconds(a.timestamp) : null;
                        const isActive = seg.index === activeIndex && activeActionIndex === i;
                        const clickable = ts !== null;
                        const handleJump = () => {
                          if (ts !== null) seekTo(ts);
                        };
                        const onKey: React.KeyboardEventHandler<HTMLLIElement> = e => {
                          if (!clickable) return;
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleJump();
                          }
                        };
                        return (
                          <li
                            key={"act-" + i}
                            ref={el => {
                              (actionRefMap.current[seg.index] ||= {});
                              actionRefMap.current[seg.index][i] = el;
                            }}
                            className={
                              (isActive
                                ? "bg-primary/10 border-l-2 border-primary"
                                : "hover:bg-accent/60 border-l-2 border-transparent") +
                              (clickable ? " cursor-pointer" : "") +
                              " pl-2 pr-2 py-1 rounded flex items-center gap-2"
                            }
                            role={clickable ? "button" : undefined}
                            tabIndex={clickable ? 0 : -1}
                            title={clickable && a.timestamp ? `跳转到 ${a.timestamp}` : undefined}
                            onClick={clickable ? handleJump : undefined}
                            onKeyDown={onKey}
                          >
                            {a.timestamp && (
                              <Button
                                size="sm"
                                variant={isActive ? "default" : "secondary"}
                                onClick={e => {
                                  e.stopPropagation();
                                  if (ts !== null) seekTo(ts);
                                }}
                              >
                                {a.timestamp}
                              </Button>
                            )}
                            <span className="text-left flex-1">{a.action}</span>
                            {isActive && <span className="text-xs text-primary font-medium">现在</span>}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
                {seg.item.analysis && (
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <div>
                      <div className="text-xs font-medium text-muted-foreground mb-1">顺畅点</div>
                      <p className="text-sm leading-relaxed">
                        {seg.item.analysis.smoothPoints ?? seg.item.analysis.smoothPointsAndInterests ?? "-"}
                      </p>
                    </div>
                    <div>
                      <div className="text-xs font-medium text-muted-foreground mb-1">摩擦点</div>
                      <p className="text-sm leading-relaxed">
                        {seg.item.analysis.frictionPoints ?? seg.item.analysis.difficultiesAndConfusion ?? "-"}
                      </p>
                    </div>
                  </div>
                )}

                {/* Optional richer sections */}
                {Array.isArray(seg.item.highlights) && seg.item.highlights.length > 0 && (
                  <div className="mt-3">
                    <div className="text-xs font-medium text-muted-foreground mb-1">高光时刻</div>
                    <ul className="list-disc pl-5 space-y-1 text-sm text-left">
                      {seg.item.highlights.map((h, i) => (
                        <li key={i}>{h}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {seg.item.metrics && Object.keys(seg.item.metrics).length > 0 && (
                  <div className="mt-3">
                    <div className="text-xs font-medium text-muted-foreground mb-1">指标</div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      {Object.entries(seg.item.metrics).map(([k, v]) => (
                        <div key={k} className="flex items-center justify-between gap-2 border rounded-md px-2 py-1">
                          <span className="text-muted-foreground">{k}</span>
                          <span className="font-medium">{String(v)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {Array.isArray(seg.item.annotations) && seg.item.annotations.length > 0 && (
                  <div className="mt-3">
                    <div className="text-xs font-medium text-muted-foreground mb-1">时间批注</div>
                    <ul className="space-y-1 text-sm">
                      {seg.item.annotations.map((a, i) => (
                        <li key={i} className="flex items-center gap-2">
                          {a.at && (
                            <Button size="sm" variant="secondary" onClick={() => seekTo(parseTimeToSeconds(a.at!))}>
                              {a.at}
                            </Button>
                          )}
                          <span className="text-left">{a.note}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {Array.isArray(seg.item.links) && seg.item.links.length > 0 && (
                  <div className="mt-3">
                    <div className="text-xs font-medium text-muted-foreground mb-1">相关链接</div>
                    <ul className="list-disc pl-5 space-y-1 text-sm text-left">
                      {seg.item.links.map((l, i) => (
                        <li key={i} className="flex items-center gap-2">
                          {l.at && (
                            <Button size="sm" variant="outline" onClick={() => seekTo(parseTimeToSeconds(l.at!))}>
                              {l.at}
                            </Button>
                          )}
                          {l.url ? (
                            <a className="text-primary underline underline-offset-4" href={l.url} target="_blank" rel="noreferrer">
                              {l.label || l.url}
                            </a>
                          ) : (
                            <span>{l.label}</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Bottom Drawer: Onboarding Analysis + Recommendations */}
      {(() => {
        const onboarding = (data.sections?.onboardingDesignAnalysis ?? (data as any).onboardingDesignAnalysis) as
          | AnalysisJson["sections"]["onboardingDesignAnalysis"]
          | undefined;
        const recs = (data.sections?.actionableRecommendations ?? (data as any).actionableRecommendations) as
          | NonNullable<AnalysisJson["sections"]>["actionableRecommendations"]
          | undefined;

        const handleHeight = DRAWER_HANDLE;

        return (
          <div className="fixed inset-x-0 bottom-0 z-50">
            <div
              className="mx-4 md:mx-6 rounded-t-xl border bg-card shadow-xl transition-[height] duration-300"
              style={{ height: drawerOpen ? `${drawerHeight}px` : `${handleHeight}px` }}
            >
              {/* Handle / Header */}
              <div
                className="relative flex items-center justify-center h-12 select-none cursor-pointer"
                onClick={() => setDrawerOpen(v => !v)}
              >
                <div className="absolute inset-x-0 top-1 flex justify-center">
                  <div className="h-1.5 w-10 rounded-full bg-muted" />
                </div>
                <div className="text-sm text-muted-foreground">{drawerOpen ? "点击收起" : "点击展开 · 洞察与建议"}</div>
              </div>

              {/* Content */}
              <div className="h-[calc(100%-48px)] overflow-auto p-4 space-y-6">
                {onboarding && (
                  <section>
                    <h2 className="text-base font-semibold mb-3">Onboarding 设计分析</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {(
                        [
                          ["信息架构与流程", onboarding.informationArchitectureAndFlow],
                          ["引导与教育", onboarding.guidanceAndEducation],
                          ["UI 与文案", onboarding.uiAndCopywriting],
                          ["价值主张", onboarding.valueProposition],
                        ] as const
                      ).map(([label, section], i) =>
                        section ? (
                          <Card key={i} className="">
                            <CardHeader className="pb-2">
                              <CardTitle className="text-sm">{label}</CardTitle>
                            </CardHeader>
                            <CardContent className="pt-0">
                              {section.pros && section.pros.length > 0 && (
                                <div className="mb-2">
                                  <div className="text-xs text-emerald-600 mb-1">优点</div>
                                  <ul className="list-disc pl-5 text-sm space-y-1">
                                    {section.pros.map((t, idx) => (
                                      <li key={idx}>{t}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              {section.cons && section.cons.length > 0 && (
                                <div>
                                  <div className="text-xs text-rose-600 mb-1">不足</div>
                                  <ul className="list-disc pl-5 text-sm space-y-1">
                                    {section.cons.map((t, idx) => (
                                      <li key={idx}>{t}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        ) : null,
                      )}
                    </div>
                  </section>
                )}

                {recs && (
                  <section>
                    <h2 className="text-base font-semibold mb-3">可执行优化建议</h2>
                    <div className="space-y-3">
                      {recs.map((r, i) => (
                        <Card key={(r?.id as any) ?? i}>
                          <CardHeader className="pb-2">
                            <CardTitle className="text-sm">
                              {(typeof r?.id !== "undefined" ? `#${r.id} ` : "") + (r?.title ?? "建议")}
                            </CardTitle>
                            {r?.problem && <CardDescription className="text-xs">问题：{r.problem}</CardDescription>}
                          </CardHeader>
                          <CardContent className="pt-0 text-sm space-y-2">
                            {r?.recommendation && (
                              <div>
                                <div className="text-xs font-medium text-muted-foreground mb-1">建议</div>
                                <p className="leading-relaxed whitespace-pre-wrap">{r.recommendation}</p>
                              </div>
                            )}
                            {r?.expectedOutcome && (
                              <div>
                                <div className="text-xs font-medium text-muted-foreground mb-1">预期效果</div>
                                <p className="leading-relaxed">{r.expectedOutcome}</p>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </section>
                )}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

export default App;

// Bottom Drawer UI is implemented inside App's JSX below
